import http from '@/utils/http'
import { getDeviceCode } from '@mp/utils'
import axios from 'axios'

type PrincipalType = 'agent' | 'platform' | 'reseller' | 'merchant' | 'shop'

// 登录预校验
export async function LoginPreCheck(params: {
  needPwdWidget: boolean
}): Promise<{
  needCaptcha: boolean // 是否需要校验
  tempToken: string
  pwdWidget: any
}> {
  return http.post('/v4/login/preCheck', {
    deviceId: await getDeviceCode(),
    tokenKey: String(new Date().getTime()),
    needPwdWidget: params?.needPwdWidget || false,
  }).then(res => res?.data)
}

// 手机验证码登录(发送验证码)
export type CaptchaVerifyProps = {
  bgImageHeight?: number
  bgImageWidth?: number
  endSlidingTime?: string
  startSlidingTime?: string
  trackList?: { t: number; type: string; x: number; y: number }[]
  validCode?: string
}
export function SendSMS(params: {
  mobileNo: string
  thirdCaptchaId?: string
  tempToken: string
}): Promise<{
  smsResendToken: string
  smsVerifyTicket: string
}> {
  return http.post('/v4/login/mobile', {
    ...params,
  }).then(res => res?.data)
}

// 手机验证码登录认证
export function LoginMobileAuth(params: {
  smsVerifyCode: string
  smsVerifyTicket: string
}): Promise<{
  loginTicket: string
  loginAwaitSelectors: LoginAwaitSelectorItem[]
}> {
  return http.post('/v4/login/mobile/auth', params).then(res => res?.data)
}

// 获取简单的图片验证码
export type LoginAwaitSelectorItem = {
  ext: {
    belongMchId: string
    belongMchName: string
    operatorType: string
  }
  displayPrincipalName: string
  operatorId: string
  operatorName: string
  operatorPrincipalType: string
  operatorStatus: string
  whetherDefaultOperator: boolean // true为管理员
}
export function CaptchaSimple(params: {
  tempToken: string
  width: number
  height: number
}): Promise<{
  captchaImageBase64: string
  loginAwaitSelectors: LoginAwaitSelectorItem[]
}> {
  return http.post('/captcha/simple', params).then(res => res?.data)
}

// 无密登录
export function LoginTicketConfirm(params: {
  loginTicket: string
  operatorId: string
}): Promise<{
  accessToken: string
  expiredAtTs: number // token到期的时间戳
  lastLoginTimeTs: number // 上次登录时间
  loginName: string
  operatorId: string
  principalDisplayName: string
  principalId: string
  principalType: PrincipalType
}> {
  return http.post('/v4/login/ticket/confirm', params).then(res => res?.data)
}

export interface LoginConfirmProps {
  accessToken: string
  mobileNoMask: string
  needTwoFactorAuth: boolean
  smsResendTicket: string
  smsVerifyTicket: string
  twoFactorAuthTicket: string
  twoFactorAuthType: 'SMS_VERIFY' | 'PASSWORD_VERIFY'
}

// #账号密码登录-ticket授权登录
export function LoginPwdTicketConfirm(params: {
  loginTicket: string
  operatorId: string
}): Promise<LoginConfirmProps> {
  return http.post('/v4/login/pwd/ticket/confirm', params).then(res => res?.data)
}

// #手机验证码登录-ticket授权登录
export function LoginMobileTicketConfirm(params: {
  loginTicket: string
  operatorId: string
}): Promise<LoginConfirmProps> {
  return http.post('/v4/login/mobile/ticket/confirm', params).then(res => res?.data)
}

// #账号密码登录-双因素认证-重发短信验证码
export function LoginPwdTfaSmsResend(params: {
  smsResendTicket: string
}): Promise<{
  mobileNoMask: string
  smsResendTicket: string
  smsVerifyTicket: string
}> {
  return http.post('/v4/login/pwd/tfa/sms/resend', params).then(res => res?.data)
}

// #账号密码登录-双因素认证-短信验证码校验
export function LoginPwdTfaSmsVerify(params: {
  smsVerifyCode: string
  smsVerifyTicket: string
  twoFactorAuthTicket: string
}) {
  return http.post('/v4/login/pwd/tfa/sms/verify', params).then(res => res?.data)
}

// 授权登录
export function LoginTokenAuth(params: {
  accessToken: string
  deviceId: string
}): Promise<{
  resultCode: 'FAIL' | 'SUCCESS'
  errorCodeDes: string
  errorCode: string
  loginNew: 'N' | 'Y'
}> {
  return axios.post('/sdppro/api/login/token/auth', { ...params, appId: 'ZF2009' }).then(res => res?.data)
}

// 账号密码登录
export function LoginPwd(params: {
  loginName: string
  loginPwd: string
  loginType: 1 | 2 // ?登录类型(1:管理员;2:操作员登录)
  operatorLoginName: string
  tempToken: string
  captchaVerify?: CaptchaVerifyProps
}) {
  return http.post('/login/pwd', params).then<{
    accessToken: string
    expiredAtTs: number
    loginName: string
    operatorId: string
    principalDisplayName: String
    principalId: string
    principalType: PrincipalType
  }>(res => res?.data)
}

// #账号密码登录(单登录账户)
export function LoginPwdSingle(params: {
  loginName: string
  loginPwd: string
  tempToken: string
  thirdCaptchaId?: string
  certSerialNo?: string // 证书序列号
  ciphertext?: string // 加密后的密码
  randomKey?: string // 随机数
}): Promise<{
  loginTicket: string
  loginAwaitSelectors: LoginAwaitSelectorItem[]
}> {
  return http.post('/v4/login/pwd/single', params).then(res => res?.data)
}

// #获取困难的验证码参数
export function GetDifficultCaptcha() {
  return http.post('/v4/captcha/getDifficultCaptcha')
}

// #手机验证码登录-双因素认证-密码验证预校验
export function LoginMobileTfaPreCheck(params: {
  needPwdWidget: boolean
  twoFactorAuthTicket: string
}): Promise<{
  needPwdWidget: boolean
  pwdWidget: any
}> {
  return http.post('/v4/login/mobile/tfa/precheck', params).then(res => res?.data)
}

// #手机验证码登录-双因素认证-密码校验
export function LoginMobileTfaPwdVerify(params: {
  certSerialNo?: string
  ciphertext?: string
  loginPwd?: string
  randomKey?: string
  twoFactorAuthTicket: string
}): Promise<{
  accessToken: string
  expiredAtTs: number
  lastLoginIp: string
  lastLoginTimeTs: number
  loginName: string
  operatorId: string
  principalDisplayName: string
  principalId: string
  principalType: string
}> {
  return http.post('/v4/login/mobile/tfa/pwd/verify', params).then(res => res?.data)
}

// 更换token接口
export function ChangeLoginToken(params: {
  accessToken: string
  deviceId: string
}) {
  return axios.post('/sdppro/api/login/token/change', params).then(res => res?.data)
}
